import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import { navigateAfterSignIn } from '../../lib/pending-oauth.js';
import {
    COMPLETION_LINKED,
    completionOutcome,
    isLinkPendingToken,
    knownEmailFromPendingToken,
    providerErrorMessage,
    providerLabel,
} from '../../lib/providers.js';
import { router } from '../../lib/router.js';
import { css, html } from '../../vendor/lit.min.js';

// Where a provider round trip lands. The API has already done the deciding and
// put the outcome in the query string; this view only redeems it:
//
//   ?token=…          the sign-in worked — swap it for a real session
//   ?pending_token=…  the provider gave us no email — collect and verify one
//   ?error=…          refused, with a reason code
// The pending token lives only in the URL the API redirected to, so a reload
// or a stray navigation would otherwise strand a half-finished connect — the
// emailed code is still perfectly valid, but nothing is left to say what it is
// for. Bridge it through sessionStorage, the same way a pending authorize
// request survives the sign-in screens.
const PENDING_TOKEN_KEY = 'd3auth_provider_pending';

class ProviderCompleteView extends BaseComponent {
    static properties = {
        status: { type: String, state: true },
        error: { type: String, state: true },
        provider: { type: String, state: true },
        email: { type: String, state: true },
        code: { type: String, state: true },
        codeSent: { type: Boolean, state: true },
        loading: { type: Boolean, state: true },
        knownEmail: { type: String, state: true },
        linking: { type: Boolean, state: true },
        returnTo: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        .provider-card { max-width: 420px; margin: 0 auto; }
        .code-input { font-size: 1.5rem; text-align: center; letter-spacing: 0.5rem; }
        .address {
            text-align: center;
            font-weight: 500;
            word-break: break-all;
            background: #f8f9fa;
            border-radius: 0.375rem;
            padding: 0.625rem 0.75rem;
        }
    `;

    constructor() {
        super();
        this.status = 'working';
        this.error = '';
        this.provider = '';
        this.email = '';
        this.code = '';
        this.codeSent = false;
        this.loading = false;
        this.knownEmail = '';
        this.linking = false;
        this.returnTo = '';
        this._pendingToken = '';
    }

    async connectedCallback() {
        super.connectedCallback();

        const match = window.location.pathname.match(/^\/oauth\/([^/]+)\/complete/);
        this.provider = match ? match[1] : '';

        const query = router.getQuery();
        // Only same-site paths: this comes back through a redirect, so an
        // absolute URL here would be an open redirect.
        const returnTo = query.get('return_to') || '';
        this.returnTo = returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : '';

        const errorCode = query.get('error');
        if (errorCode) {
            this.status = 'error';
            this.error = providerErrorMessage(errorCode);
            return;
        }

        const pendingToken =
            query.get('pending_token') || sessionStorage.getItem(PENDING_TOKEN_KEY);
        if (pendingToken) {
            sessionStorage.setItem(PENDING_TOKEN_KEY, pendingToken);

            // Followed the link from the email: it carries the same proof the
            // code does, so finish rather than asking for something they have
            // already provided.
            const emailedToken = query.get('token');
            if (emailedToken) {
                this._pendingToken = pendingToken;
                this.linking = isLinkPendingToken(pendingToken);
                this.status = 'working';
                await this._completeWithProof({ token: emailedToken });
                return;
            }
            this._pendingToken = pendingToken;
            this.knownEmail = knownEmailFromPendingToken(pendingToken);
            this.email = this.knownEmail;
            // Linking fixes the address: the challenge only means anything if
            // it goes to the one the provider asserted, so the field is shown
            // read-only and the server rejects anything else.
            this.linking = isLinkPendingToken(pendingToken);
            this.status = 'email';
            return;
        }

        // The API made the link itself (a connect-only provider, or a
        // reconnect): nothing to redeem, so go back to where the user started.
        if (completionOutcome(query) === COMPLETION_LINKED) {
            router.navigate(this.returnTo || '/profile');
            return;
        }

        const token = query.get('token');
        if (!token) {
            this.status = 'error';
            this.error = providerErrorMessage('provider_failed');
            return;
        }

        await this._redeem(token);
    }

    async _redeem(token) {
        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/provider/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Sign-in could not be completed');
            }
            this._signIn(data);
        } catch (err) {
            this.status = 'error';
            this.error = err.message;
        }
    }

    // Hand off to the shared post-sign-in routing so an app waiting on an
    // authorize request gets resumed instead of being dropped on the profile.
    _signIn(data) {
        sessionStorage.removeItem(PENDING_TOKEN_KEY);
        authState.setTokens(data.tokens);
        authState.user = data.user;
        authState.isAuthenticated = true;
        authState.notify();
        navigateAfterSignIn(sessionStorage, authState.consentsOutstanding);
    }

    async _handleSendCode(e) {
        e.preventDefault();
        this.error = '';
        this.loading = true;
        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/provider/send-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pending_token: this._pendingToken, email: this.email }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'Could not send a code to that address');
            }
            this.codeSent = true;
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    async _handleVerify(e) {
        e.preventDefault();
        this.loading = true;
        try {
            await this._completeWithProof({ email: this.email, code: this.code });
        } finally {
            this.loading = false;
        }
    }

    // The code and the emailed token are the same proof by different routes, so
    // both finish here.
    async _completeWithProof(proof) {
        this.error = '';
        try {
            const response = await fetch(`${getBackendUrl()}/api/v1/provider/complete-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pending_token: this._pendingToken, ...proof }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || 'That did not work');
            }
            if (data.linked) {
                // A connect, not a sign-in — the session we already have stands.
                sessionStorage.removeItem(PENDING_TOKEN_KEY);
                router.navigate(this.returnTo || '/profile');
                return;
            }
            this._signIn(data);
        } catch (err) {
            this.status = 'error';
            this.error = err.message;
        }
    }

    render() {
        return html`
            <div class="provider-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        ${this._renderBody()}
                    </div>
                </div>
            </div>
        `;
    }

    _renderBody() {
        if (this.status === 'working') {
            return html`
                <div class="text-center">
                    <div class="spinner-border text-primary" role="status"></div>
                    <p class="mt-3 text-muted">Finishing sign-in…</p>
                </div>
            `;
        }

        if (this.status === 'error') {
            // A failure here may have been a connect rather than a sign-in, so
            // send the user back where they started when we know it.
            const back = this.returnTo || '/';
            return html`
                <h3 class="card-title text-center mb-3">
                    ${this.returnTo ? "Couldn't connect" : 'Sign In'}
                </h3>
                <div class="alert alert-danger">${this.error}</div>
                <a href="${back}" class="btn btn-outline-primary w-100">
                    ${this.returnTo ? 'Go back' : 'Back to Sign In'}
                </a>
            `;
        }

        return html`
            <h3 class="card-title text-center mb-2">
                ${this.knownEmail ? "Confirm it's you" : 'One more step'}
            </h3>
            <p class="text-muted text-center mb-4">
                ${this._renderBlurb()}
            </p>
            ${this.error ? html`<div class="alert alert-danger">${this.error}</div>` : ''}
            ${this.codeSent ? this._renderCodeStep() : this._renderEmailStep()}
        `;
    }

    _renderBlurb() {
        if (this.linking) {
            return html`We'll email a code to confirm this ${providerLabel(this.provider)} account is
                yours.`;
        }
        if (this.knownEmail) {
            return html`We found an existing account for this address. Verify it and we'll connect
                ${providerLabel(this.provider)} to it.`;
        }
        return html`${providerLabel(this.provider)} didn't share an email address, so we need one to
            finish setting up your account.`;
    }

    _renderEmailStep() {
        // Linking fixes the address, so it is shown as a fact rather than an
        // input the user might expect to change.
        if (this.linking) {
            return html`
                <form @submit="${this._handleSendCode}">
                    <div class="address mb-3">${this.knownEmail}</div>
                    <button type="submit" class="btn btn-primary w-100" ?disabled="${this.loading}">
                        ${this.loading ? 'Sending…' : 'Send code'}
                    </button>
                </form>
            `;
        }

        return html`
            <form @submit="${this._handleSendCode}">
                <div class="mb-3">
                    <label for="provider-email" class="form-label">Email</label>
                    <input type="email" class="form-control" id="provider-email" required autofocus
                        .value="${this.email}"
                        @input="${(e) => (this.email = e.target.value)}">
                </div>
                <button type="submit" class="btn btn-primary w-100" ?disabled="${this.loading}">
                    ${this.loading ? 'Sending…' : 'Send code'}
                </button>
            </form>
        `;
    }

    _codeButtonLabel() {
        if (this.linking) {
            return this.loading ? 'Connecting…' : 'Connect account';
        }
        return this.loading ? 'Verifying…' : 'Finish sign in';
    }

    _renderCodeStep() {
        return html`
            <form @submit="${this._handleVerify}">
                <div class="alert alert-info">A sign-in code has been sent to your email.</div>
                <div class="mb-3">
                    <label for="provider-code" class="form-label">Enter code</label>
                    <input type="text" class="form-control code-input" id="provider-code" required autofocus
                        maxlength="6" inputmode="numeric" pattern="[0-9]{6}" autocomplete="one-time-code"
                        .value="${this.code}"
                        @input="${(e) => (this.code = e.target.value.replace(/\D/g, ''))}">
                </div>
                <button type="submit" class="btn btn-primary w-100"
                    ?disabled="${this.loading || this.code.length !== 6}">
                    ${this._codeButtonLabel()}
                </button>
            </form>
        `;
    }
}

customElements.define('provider-complete-view', ProviderCompleteView);
export { ProviderCompleteView };
